import type { SceneElement } from "@calcplan/domain";

/**
 * O que um módulo É, contado da árvore de componentes que a importação já
 * registra (engineeringMeshBindings → componentPath): portas, gavetas,
 * basculantes, prateleiras, nichos.
 *
 * Nasceu da rodada de 21/08 11:24: o pedido dizia "móvel de 3 portas", a cena
 * só oferecia nomes ("Movel 1 - Completo", "Balcão Gaveteiro") e o agente
 * vinculou a cuba ao gaveteiro. As gavetas estavam gravadas nas extensões —
 * `Gaveta copy 001…003` — e ninguém as lia. Nome descreve, id identifica, e a
 * árvore de componentes DIZ o que o móvel tem.
 *
 * Um SKP simples, sem componentes dinâmicos, não tem árvore: devolve null, e
 * aí só a foto confirma o atributo. Isso é informação, não falha.
 */
export interface ElementComponentry {
  portas?: number;
  gavetas?: number;
  basculantes?: number;
  prateleiras?: number;
  nichos?: number;
}

export type ComponentryFamily = keyof ElementComponentry;

/** Vocabulário do catálogo (SKP em português): o nó do conjunto começa pelo nome da família. */
const FAMILY_PREFIXES: ReadonlyArray<readonly [ComponentryFamily, ReadonlyArray<string>]> = [
  ["portas", ["porta", "portas"]],
  ["gavetas", ["gaveta", "gavetas", "gavetao", "gavetoes"]],
  ["basculantes", ["basculante", "basculantes", "basc"]],
  ["prateleiras", ["prateleira", "prateleiras"]],
  ["nichos", ["nicho", "nichos"]],
];

export const FAMILY_LABELS: Record<ComponentryFamily, { singular: string; plural: string }> = {
  portas: { singular: "porta", plural: "portas" },
  gavetas: { singular: "gaveta", plural: "gavetas" },
  basculantes: { singular: "basculante", plural: "basculantes" },
  prateleiras: { singular: "prateleira", plural: "prateleiras" },
  nichos: { singular: "nicho", plural: "nichos" },
};

function normalizeSegment(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("pt-BR")
    .replace(/[_\-.]+/g, " ")
    .trim();
}

function familyOfSegment(segment: string): ComponentryFamily | null {
  const normalized = normalizeSegment(segment);
  const firstWord = normalized.split(/\s+/)[0] ?? "";
  for (const [family, prefixes] of FAMILY_PREFIXES) {
    if (prefixes.includes(firstWord)) return family;
  }
  return null;
}

interface MeshBindingLike {
  componentPath?: unknown;
}

/**
 * Conta nós de conjunto por família. Cada caminho contribui com o nó MAIS
 * EXTERNO de cada família (a "Gaveta copy 002" conta uma vez, não uma por
 * frente, fundo e lateral que ela contém).
 */
export function elementComponentry(
  element: Pick<SceneElement, "extensions">,
): ElementComponentry | null {
  const bindings = element.extensions?.engineeringMeshBindings;
  if (!Array.isArray(bindings) || !bindings.length) return null;
  const nodesByFamily = new Map<ComponentryFamily, Set<string>>();
  let sawPath = false;
  for (const binding of bindings as MeshBindingLike[]) {
    const path = typeof binding?.componentPath === "string" ? binding.componentPath : null;
    if (!path) continue;
    sawPath = true;
    const segments = path.split(" / ");
    const seen = new Set<ComponentryFamily>();
    for (let index = 0; index < segments.length; index += 1) {
      const family = familyOfSegment(segments[index]!);
      if (!family || seen.has(family)) continue;
      seen.add(family);
      const nodeKey = segments.slice(0, index + 1).join(" / ");
      const bucket = nodesByFamily.get(family) ?? new Set<string>();
      bucket.add(nodeKey);
      nodesByFamily.set(family, bucket);
    }
  }
  if (!sawPath) return null;
  const result: ElementComponentry = {};
  for (const [family, nodes] of nodesByFamily) {
    if (nodes.size) result[family] = nodes.size;
  }
  return result;
}

/** "4 gavetas, nenhuma porta" — ou a ausência de árvore, dita com todas as letras. */
export function describeComponentry(componentry: ElementComponentry | null | undefined): string {
  if (!componentry) return "sem árvore de componentes registrada (só a foto confirma portas/gavetas)";
  const present = (Object.keys(FAMILY_LABELS) as ComponentryFamily[])
    .filter((family) => (componentry[family] ?? 0) > 0)
    .map((family) => {
      const count = componentry[family]!;
      return `${count} ${count === 1 ? FAMILY_LABELS[family].singular : FAMILY_LABELS[family].plural}`;
    });
  if (!present.length) return "árvore de componentes sem portas, gavetas, basculantes, prateleiras ou nichos";
  return present.join(", ");
}
